import { VALID_SORT_KEYS } from './columnRegistry';
import { RETIRED_MISSING_INFO_KEYS } from '../../utils/equipmentQuality';

/**
 * Plan 101 — encode/decode helpers for the Fleet Explorer URL params.
 *
 * URL search params remain the session source of truth: filters travel as one
 * `f.<key>=<value>` param each, the sort stack as `sort=key:dir,key:dir`
 * (≤3 levels). The same encoding is stored in deviceIndexPrefs so hydration
 * is a straight copy.
 */

export interface SortLevel {
  key: string;
  dir: 'asc' | 'desc';
}

export const MAX_SORT_LEVELS = 3;

export const FILTER_PARAM_PREFIX = 'f.';

/** Non-filter params that mark a URL as "explorer state" for hydration.
 *  FS-889: `report` (the open report) and `rv` ("this URL carries the whole
 *  data view") count too — a URL carrying either is not a fresh visit. */
const EXPLORER_PARAM_KEYS = new Set(['q', 'page', 'page_size', 'sort', 'dir', 'report', 'rv']);

/**
 * Pre-101 facet params → their typed-filter keys. Old bookmarks keep working:
 * these read as the equivalent `f.<key>` filter (unless it is explicitly
 * present too) and are rewritten to the new form on mount.
 */
export const LEGACY_FILTER_ALIASES: Record<string, string> = {
  entity_prefix: 'entity_path',
  context_status: 'context_status',
  app_owner_uid: 'app_owner_uid',
  app_location_id: 'app_location_id',
  app_hvac_id: 'app_hvac_id',
};

/**
 * FS-752 — filter values the server no longer recognises, per filter key.
 * Missing info stopped listing photos and renamed the filter key, so a saved
 * view or a bookmark naming one of these would filter on something no row
 * carries. They are dropped, never mapped: "missing a filter photo" and
 * "missing filter information" are different questions.
 */
const RETIRED_FILTER_VALUES: Record<string, readonly string[]> = {
  app_missing_fields: RETIRED_MISSING_INFO_KEYS,
};

/**
 * One filter's comma-joined value with its retired values taken out. Returns
 * '' when nothing is left, which every caller treats as "remove the filter".
 *
 * For a key that has retired values, every token is trimmed and empty tokens
 * go too: `filter_curve, ` must not leave a lone space behind, which the API
 * rejects as an empty filter value. A key with none is returned untouched.
 */
export function dropRetiredFilterValue(key: string, value: string): string {
  const retired = RETIRED_FILTER_VALUES[key];
  if (!retired) return value;
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '' && !retired.includes(v))
    .join(',');
}

/** Every filter in a {key: value} record, retired values dropped. */
export function dropRetiredFilterValues(filters: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    const kept = dropRetiredFilterValue(k, v);
    if (kept !== '') out[k] = kept;
  }
  return out;
}

/**
 * All active `f.<key>` params as a plain {key: rawValue} record, with legacy
 * facet params folded in (an explicit f.<key> wins over its alias). Retired
 * values are dropped here too, so the first fetch never sends one even before
 * `normalizeLegacyParams` has rewritten the URL.
 */
export function filtersFromSearchParams(sp: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  sp.forEach((value, param) => {
    if (param.startsWith(FILTER_PARAM_PREFIX) && value !== '') {
      out[param.slice(FILTER_PARAM_PREFIX.length)] = value;
    }
  });
  for (const [legacy, key] of Object.entries(LEGACY_FILTER_ALIASES)) {
    const v = sp.get(legacy);
    if (v && out[key] === undefined) out[key] = v;
  }
  return dropRetiredFilterValues(out);
}

/** True when the URL carries no explorer state at all (fresh navigation). */
export function isExplorerUrlBare(sp: URLSearchParams): boolean {
  let bare = true;
  sp.forEach((_v, param) => {
    if (
      EXPLORER_PARAM_KEYS.has(param) ||
      LEGACY_FILTER_ALIASES[param] !== undefined ||
      param.startsWith(FILTER_PARAM_PREFIX)
    ) {
      bare = false;
    }
  });
  return bare;
}

/**
 * Rewrite legacy facet params to their `f.<key>` form (and the legacy
 * sort/dir pair to `sort=key:dir`), and drop retired filter values. Returns null when the URL is already
 * normalized so callers can skip a history replace.
 */
export function normalizeLegacyParams(sp: URLSearchParams): URLSearchParams | null {
  let changed = false;
  const next = new URLSearchParams(sp);
  for (const [legacy, key] of Object.entries(LEGACY_FILTER_ALIASES)) {
    const v = next.get(legacy);
    if (v === null) continue;
    if (v !== '' && !next.has(FILTER_PARAM_PREFIX + key)) {
      next.set(FILTER_PARAM_PREFIX + key, v);
    }
    next.delete(legacy);
    changed = true;
  }
  // FS-752: a filter value the server retired is removed from the URL, and
  // the whole param when it was the only value.
  for (const key of Object.keys(RETIRED_FILTER_VALUES)) {
    const param = FILTER_PARAM_PREFIX + key;
    const v = next.get(param);
    if (v === null) continue;
    const kept = dropRetiredFilterValue(key, v);
    if (kept === v) continue;
    if (kept) next.set(param, kept);
    else next.delete(param);
    changed = true;
  }
  const sort = next.get('sort');
  if (sort !== null || next.has('dir')) {
    const levels = parseSortParam(sort, next.get('dir'));
    const serialized = serializeSort(levels);
    if ((sort ?? '') !== serialized) {
      if (serialized) next.set('sort', serialized);
      else next.delete('sort');
      changed = true;
    }
    if (next.has('dir')) {
      next.delete('dir');
      changed = true;
    }
  }
  return changed ? next : null;
}

/**
 * Parse `?sort=` into validated levels. Accepts the Plan 101 `key:dir,...`
 * format and the legacy single-key form (`sort=activated_at&dir=desc`).
 * Unknown keys/dirs and extra levels are dropped so junk in the URL is never
 * forwarded to the backend.
 */
export function parseSortParam(raw: string | null, legacyDir?: string | null): SortLevel[] {
  const sort = (raw ?? '').trim();
  if (!sort) return [];

  if (!sort.includes(':') && !sort.includes(',')) {
    if (!VALID_SORT_KEYS.has(sort)) return [];
    return [{ key: sort, dir: legacyDir === 'desc' ? 'desc' : 'asc' }];
  }

  const seen = new Set<string>();
  const levels: SortLevel[] = [];
  for (const part of sort.split(',')) {
    if (levels.length >= MAX_SORT_LEVELS) break;
    const [key, dirPart] = part.trim().split(':');
    const dir = dirPart === 'desc' ? 'desc' : 'asc';
    if (!key || !VALID_SORT_KEYS.has(key) || seen.has(key)) continue;
    seen.add(key);
    levels.push({ key, dir });
  }
  return levels;
}

export function serializeSort(levels: SortLevel[]): string {
  return levels.map((l) => `${l.key}:${l.dir}`).join(',');
}

/** Split a `min..max` / `from..to` range value into its two open-able ends. */
export function splitRange(value: string): [string, string] {
  const idx = value.indexOf('..');
  if (idx === -1) return [value, ''];
  return [value.slice(0, idx), value.slice(idx + 2)];
}

/** Join range ends back into the wire format; empty when both ends open. */
export function joinRange(lo: string, hi: string): string {
  if (!lo && !hi) return '';
  return `${lo}..${hi}`;
}
